import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const BusinessContext = createContext();

export function BusinessProvider({ children }) {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load businesses on mount. There's nothing to load for a signed-out
  // visitor, but `loading` still has to resolve to false in that case —
  // otherwise it stays stuck at its initial `true` forever, which blocks
  // Dashboard.jsx's own redirect-to-login effect (gated behind this).
  useEffect(() => {
    if (user?.email) {
      loadBusinesses();
    } else {
      setLoading(false);
    }
  }, [user?.email]);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/businesses?owner_email=${user.email}`,
        { credentials: 'include' }
      );

      // Only process if response is ok
      if (!response.ok) {
        console.error('Failed to load businesses: ' + response.status);
        setBusinesses([]);
        setCurrentBusiness(null);
        return;
      }

      const data = await response.json();
      setBusinesses(Array.isArray(data) ? data : []);

      // Set first business as default or load from localStorage
      const saved = localStorage.getItem('currentBusinessId');
      if (saved && data.find(b => b.id === saved)) {
        setCurrentBusiness(data.find(b => b.id === saved));
      } else if (data.length > 0) {
        setCurrentBusiness(data[0]);
        localStorage.setItem('currentBusinessId', data[0].id);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error);
      setBusinesses([]);
      setCurrentBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  const createBusiness = async (name, description, location) => {
    try {
      const response = await fetch('/api/businesses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name,
          description,
          location,
          owner_email: user.email
        })
      });

      const newBusiness = await response.json();
      setBusinesses([...businesses, newBusiness]);
      setCurrentBusiness(newBusiness);
      localStorage.setItem('currentBusinessId', newBusiness.id);
      return newBusiness;
    } catch (error) {
      console.error('Failed to create business:', error);
      throw error;
    }
  };

  const switchBusiness = (businessId) => {
    const business = businesses.find(b => b.id === businessId);
    if (business) {
      setCurrentBusiness(business);
      localStorage.setItem('currentBusinessId', businessId);
    }
  };

  const value = {
    businesses,
    currentBusiness,
    loading,
    createBusiness,
    switchBusiness,
    loadBusinesses
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within BusinessProvider');
  }
  return context;
}
